import { setProperties } from '@ember/object';
import EmberService, { service } from '@ember/service';

import type { CreateRecordProperties, Service } from 'webapp-types';
import { DefaultTeamColor } from 'webapp/const/board-color-config';
import { DEFAULT_CDR_GRANULATIONS, DEFAULT_CDR_RANGES } from 'webapp/const/e2e-seed-defaults';
import { ActivityLogEvent } from 'webapp/enums/activity-log';
import {
	AlertEventCondition,
	BillingAddonType,
	BlockVisualization,
	DateRangeSelectionId,
	DeliveryType,
	GoalFormatType,
	GoalProgressType,
	GoalStatus,
	GoalTargetSuccessCondition,
	GoalTargetValueType,
	GoalType,
	GranulationTypeId,
	ReminderObjectType,
	ScheduleType,
	ScriptDetailItemType,
	TeamSectionItemType,
	TeamType,
	TimeUnit,
} from 'webapp/enums/enums';
import { generateHash } from 'webapp/utils/common';
import {
	getHistoryValuesFromValueRows,
	groupValueRowsByTimeUnit,
	recalculateAllValues,
} from 'webapp/utils/goal-value-rows';
import { getCurrentUnixTimestamp } from 'webapp/utils/number';

import DateRange from 'webapp/models-custom/date-range';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type { ExpressionDetailItem } from 'webapp/models-custom/script';
import type BlockRecord from 'webapp/models/block';
import type BoardRecord from 'webapp/models/board';
import DataLoaderE2ESeed from 'webapp/data-loader/e2e-seed';

interface E2EAuthData {
	accessToken: string | null;
	userDataToken: string | null;
	accessTokenExpiry: number | null;
}

interface E2EGlobals {
	apiUrl?: string;
	connectApiUrl?: string;
	authServiceUrl?: string;
}

const CLEARED_AUTH: E2EAuthData = {
	accessToken: null,
	userDataToken: null,
	accessTokenExpiry: null,
};

// TODO: switch to a value the BE provides so we don't hardcode an env.
const FALLBACK_ENV_VERSION = 7;

export default class E2ESeedService extends EmberService {
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/goal-logs') declare mmGoalLogsService: Service.ModelManager.GoalLogs;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;
	@service('model-manager/report-schedule-settings')
	declare mmReportScheduleSettingsService: Service.ModelManager.ReportScheduleSettings;
	@service('model-manager/reminders') declare mmRemindersService: Service.ModelManager.Reminders;
	@service('model-manager/alerts') declare mmAlertsService: Service.ModelManager.Alerts;
	@service('model-manager/metric-anomalies')
	declare mmMetricAnomaliesService: Service.ModelManager.MetricAnomalies;
	@service('model-manager/scorecards')
	declare mmScorecardsService: Service.ModelManager.Scorecards;
	@service('model-manager/metric-digests')
	declare mmMetricDigestsService: Service.ModelManager.MetricDigests;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/teams') declare mmTeamsService: Service.ModelManager.Teams;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;

	// Constants the BE will not provide — DateRange formulas only live in the FE bundle.
	setBaseGlobals() {
		DateRange.setFormulas({
			cdrRanges: DEFAULT_CDR_RANGES,
			cdrGranulations: DEFAULT_CDR_GRANULATIONS,
		});
		DateRange.createPredefinedRanges();
		DateRange.createPredefinedGranulations();
	}

	setAuthData(data: E2EAuthData) {
		setProperties(this.currentUserService, data);
		const dataLoader = this.navigationService.dataLoader;
		if (dataLoader instanceof DataLoaderE2ESeed) {
			dataLoader.markInitialRequestsReady();
		}
	}

	clearAuthData() {
		setProperties(this.currentUserService, CLEARED_AUTH);
	}

	// Values the BE owns. Until the BE actually provides them, fall back to develop env 7.
	seedGlobals(data: E2EGlobals = {}) {
		const env = FALLBACK_ENV_VERSION;
		this.requestService.apiUrl = data.apiUrl ?? `https://apidevelop${env}.databox.com`;
		this.requestService.connectApiUrl = data.connectApiUrl ?? '';
		this.requestService.authServiceUrl =
			data.authServiceUrl ?? `https://auth-api-develop${env}.databox.com`;
	}

	async createDataboard(name?: string, isPublicEnabled = false, color?: string) {
		const properties: CreateRecordProperties<BoardRecord> = { name, isPublicEnabled };
		if (color) {
			properties.color = color;
			// not every color supports the default theme 1 (mirrors createNewRecord)
			properties.themeId = this.mmBoardsService.availableThemesForColor(color)[0] || 1;
		}
		const model = this.mmBoardsService.createNewModel(properties);
		await model.save();
		return model.id;
	}

	async createBlock(
		boardId: string,
		name: string,
		visualization: BlockVisualization = BlockVisualization.NUMBER,
		order = 0,
		posX = 0,
		posY = 0,
		width = 4,
		height = 4,
	) {
		const model = this.mmBlocksService.createNewModel({
			boardId: Number(boardId),
			name,
			visualization,
			order,
			posX,
			posY,
			width,
			height,
		});
		await model.save();
	}

	async createBlockFromMetric(
		boardId: string,
		metricSetupKey: string,
		metricSetupSpaceAccessId: number,
		visualization = BlockVisualization.NUMBER,
		order = 0,
		posX = 0,
		posY = 0,
		width = 4,
		height = 4,
	) {
		const properties: CreateRecordProperties<BlockRecord> = {
			boardId: Number(boardId),
			visualization,
			fromMetricSetup: { key: metricSetupKey, spaceAccessId: metricSetupSpaceAccessId },
			order,
			posX,
			posY,
			width,
			height,
		};
		if (!this.mmBlocksService.isVisualizationSeries(visualization)) {
			// only the granulation is set on create (BE reads an empty range as none)
			properties.fromMetricDateRange = {
				range: {},
				granulation: { isWholeRange: true },
			};
		}
		const model = this.mmBlocksService.createNewModel(properties);
		await model.save();
	}

	// ownerId is required: the BE rejects goals with a null owner, and in the
	// e2e-seed context current-user is not loaded, so it cannot be defaulted
	// from currentUserService.numericId the way createNewModel normally does.
	async createAutomaticGoal(
		title: string,
		mainMetricSetupKey: string,
		mainMetricSetupSpaceAccessId: number,
		ownerId: number,
		targetValue = 1000,
	) {
		const model = this.mmGoalsService.createNewModel({
			title,
			type: GoalType.GOAL,
			progressType: GoalProgressType.AUTOMATIC,
			mainMetricSetupKey,
			mainMetricSetupSpaceAccessId,
			ownerId,
			targetValue,
			targetSuccessCondition: GoalTargetSuccessCondition.REACH,
		});
		model.users = [ownerId];
		await this.applyGoalHistoryValues(
			model,
			targetValue,
			model.isMainMetricPercentage,
			model.isMainMetricInverted,
		);
		await model.save();
		return model.goalKeyLong;
	}

	async createManualGoal(
		title: string,
		ownerId: number,
		targetValue = 100,
		startValue = 0,
		currentValue?: number,
	) {
		const model = this.mmGoalsService.createNewModel({
			title,
			type: GoalType.GOAL,
			progressType: GoalProgressType.MANUAL,
			formatType: GoalFormatType.PERCENTAGE,
			ownerId,
			targetValue,
			startValue,
			targetSuccessCondition: GoalTargetSuccessCondition.REACH,
		});
		model.users = [ownerId];

		await this.applyGoalHistoryValues(model, targetValue);

		await model.save();

		if (currentValue !== undefined) {
			// Progress is BE-derived from goal logs, not a creation field — mirror
			// the "Update goal progress" popup's submit (GoalLog.status required).
			const log = this.mmGoalLogsService.createNewModel({
				goalId: model.numericId!,
				value: currentValue,
				status: GoalStatus.ON_TRACK,
				createdBy: ownerId,
				created: getCurrentUnixTimestamp(),
			});
			await this.mmGoalLogsService.createOrUpdateGoalLog(log);
		}

		return model.goalKeyLong;
	}

	// Goals require historyValues distributed across time periods (save guard).
	private async applyGoalHistoryValues(
		model: ReturnType<typeof this.mmGoalsService.createNewModel>,
		targetValue: number,
		isMainMetricPercentage = false,
		isMainMetricInverted = false,
	) {
		await this.mmGoalsService.requestFullFormLayout(model);
		const valueRows = this.mmGoalsService.updateFormLayout(
			[],
			groupValueRowsByTimeUnit([]),
			isMainMetricPercentage,
			isMainMetricInverted,
		);
		this.mmGoalsService.markRowHiddenAndClearValue(valueRows, model.valuesLastDateTs);
		const valueRowsByTimeUnit = groupValueRowsByTimeUnit(valueRows);
		// load limitations so mainMetricType is resolved before recalculating
		await model.loadMainMetricLimitations();
		recalculateAllValues(
			valueRowsByTimeUnit,
			model.primaryGranulation,
			model.isTargetValuePercentFormat,
			targetValue,
			model.startDateTsFromTimeframe,
			model.valuesLastDateTs,
			model.useEndDateForCalculations,
			model.mainMetricType,
		);
		model.historyValues = getHistoryValuesFromValueRows(valueRowsByTimeUnit);
	}

	async createObjective(title: string, ownerId: number, targetValue = 100) {
		const model = this.mmGoalsService.createNewModel({
			title,
			type: GoalType.OBJECTIVE,
			progressType: GoalProgressType.SUBGOALS,
			targetValueType: GoalTargetValueType.PERCENTAGE,
			formatType: GoalFormatType.PERCENTAGE,
			decimals: 2,
			ownerId,
			targetValue,
		});
		model.users = [ownerId];
		await model.save();
		return model.goalKeyLong;
	}

	async createObjectiveWithSubgoal(
		objectiveTitle: string,
		subgoalTitle: string,
		ownerId: number,
		targetValue = 100,
	) {
		const subgoalKey = await this.createManualGoal(subgoalTitle, ownerId);
		const subgoalRecord = this.mmGoalsService.savedRecords.find(
			(record) => record.goalKeyLong === subgoalKey,
		)!;
		const subgoal = this.mmGoalsService.createModelFromRecord(subgoalRecord);

		const objectiveKey = await this.createObjective(objectiveTitle, ownerId, targetValue);
		const objectiveRecord = this.mmGoalsService.savedRecords.find(
			(record) => record.goalKeyLong === objectiveKey,
		)!;
		const objective = this.mmGoalsService.createModelFromRecord(objectiveRecord);

		// BE drops subGoals sent on create; attaching requires a second save, same as the "Add existing goal" UI flow
		objective.subGoals = [
			{
				goalId: subgoal.numericId!,
				goalFormat: subgoal.formatType || GoalFormatType.NUMBER,
				isTimeframeValid: true,
			},
		];
		await objective.save();

		return objectiveKey;
	}

	getGoalId(goalKey: string) {
		const record = this.mmGoalsService.savedRecords.find(
			(item) => item.goalKeyLong === goalKey,
		)!;
		return this.mmGoalsService.createModelFromRecord(record).numericId;
	}

	async createReport(name?: string) {
		const model = this.mmReportsService.createNewModel(name ? { title: name } : {});
		await model.save();
		return model.id;
	}

	async createBoardShare(boardId: string, recipientEmail: string) {
		const model = this.mmBoardSharesService.createNewModel({ boardId: Number(boardId) });
		model.recipients = [recipientEmail];
		model.cleanupUnusedScheduleValues();
		await model.save();
	}

	async createReportShare(reportId: string, recipientEmail: string) {
		const model = this.mmReportScheduleSettingsService.createNewModel({
			reportId: Number(reportId),
		});
		model.recipients = [recipientEmail];
		model.cleanupUnusedScheduleValues();
		await model.save();
	}

	async createReportReminder(reportId: string) {
		const model = this.mmRemindersService.createNewModel({
			objectType: ReminderObjectType.REPORT,
			objectId: Number(reportId),
		});
		model.cleanupUnusedScheduleValues();
		await model.save();
	}

	async createManualAlert(
		metricSetupKey: string,
		metricSetupSpaceAccessId: number,
		qualifier: number,
		userId: number,
	) {
		const dateRange = DateRange.createFromRangeUISelectionIdWithGranulationId(
			DateRangeSelectionId.LAST_X,
			GranulationTypeId.TOTAL,
		);
		dateRange.rangeIncludeCurrent = false;
		dateRange.rangeTimeUnit = TimeUnit.HOUR;
		dateRange.rangeMultiplier = 24;

		const model = this.mmAlertsService.createNewModel({
			metricSetupKey,
			metricSetupSpaceAccessId,
			condition: AlertEventCondition.IS_GREATER_THAN,
			qualifier,
			deliveryType: [DeliveryType.EMAIL],
			dateRange: dateRange.getFormula(),
			userIds: [userId],
		});
		await model.save();
	}

	async createSmartAlert(metricSetupKey: string, metricSetupSpaceAccessId: number) {
		const metricSetup = new MetricSetup({
			key: metricSetupKey,
			spaceAccessId: metricSetupSpaceAccessId,
		});
		const model = this.mmMetricAnomaliesService.createModelForMetricSetup(metricSetup);
		// settings have no default in the model; topItemDefaultSettings is not loaded in the e2e-seed bridge
		model.settings = {
			anomalyScore: 20,
			granulations: { daily: true, weekly: false, monthly: false },
			notifications: { email: true, slack: [] },
		};
		await model.save();
	}

	async createMetricDigest(
		metricSetupKey: string,
		metricSetupSpaceAccessId: number,
		title: string,
		userId: number,
	) {
		const metricSetup = new MetricSetup({
			key: metricSetupKey,
			spaceAccessId: metricSetupSpaceAccessId,
		});
		const scorecard = this.mmMetricDigestsService.createModelForMetricSetup(
			metricSetup,
			'notifications',
			true,
		);
		scorecard.title = title;
		scorecard.userIds = [userId];
		scorecard.delivery = [DeliveryType.EMAIL];
		scorecard.scheduleType = ScheduleType.WEEKLY;
		await scorecard.save();
		const dataSelect = scorecard.presetDataSelectsOnCreate[0]!;
		dataSelect.blockId = scorecard.blockId!;
		await dataSelect.save();
	}

	async createScorecard(
		metricSetupKey: string,
		metricSetupSpaceAccessId: number,
		title: string,
		userId: number,
	) {
		const metricSetup = new MetricSetup({
			key: metricSetupKey,
			spaceAccessId: metricSetupSpaceAccessId,
		});
		const scorecard = this.mmScorecardsService.createModelForMetricSetup(
			metricSetup,
			'notifications',
			true,
		);
		scorecard.title = title;
		scorecard.userIds = [userId];
		scorecard.delivery = [DeliveryType.EMAIL];
		await scorecard.save();
		const dataSelect = scorecard.presetDataSelectsOnCreate[0]!;
		dataSelect.blockId = scorecard.blockId!;
		await dataSelect.save();
	}

	async createTag(name: string) {
		// color is required by the BE; getRandomColor() relies on
		// constantsService.tagColors, not loaded in the e2e-seed bridge.
		const model = this.mmTagsService.createNewModel({ name, color: 'blue' });
		await model.save();
		return model.id;
	}

	async createCalculatedMetric(
		name: string,
		operands: { key: string; spaceAccessId: number }[],
		operator = '+',
	) {
		const expressionDetails: ExpressionDetailItem[] = operands.map(
			({ key, spaceAccessId }) => ({
				id: generateHash(10),
				type: ScriptDetailItemType.METRIC,
				metricSetup: { key, spaceAccessId },
			}),
		);
		const expression = expressionDetails.map((d) => `{${d.id}}`).join(operator);

		const model = this.mmScriptsService.createNewModel({
			description: name,
			expression,
			expressionDetails,
		});
		await model.save();
		return model.id;
	}

	async logBoardView(boardId: string) {
		await this.requestService.api('POST', '/activity-logs', {
			activityLog: { objectId: Number(boardId), eventId: ActivityLogEvent.BOARD_VIEW },
		});
	}

	// ownerId required: bridge has no current-user to default it from.
	async createLoop(title: string, ownerId: number, boardId: string) {
		const model = this.mmBoardLoopsService.createNewModel({
			title,
			ownerId,
			boardIds: [Number(boardId)],
		});
		await model.save();
		return model.id;
	}

	// Space = Team. ownerId required (no current-user in bridge).
	async createSpace(name: string, ownerId: number, boardId: string) {
		const team = this.mmTeamsService.createNewModel({
			name,
			ownerId,
			color: DefaultTeamColor,
			type: TeamType.OPEN,
		});
		const teamRecord = await team.save();
		const teamId = teamRecord.id!;

		// Databoards attach to a section; create one (suppressing UI) and reload it.
		await this.mmTeamSectionsService.createNew(teamId);
		await this.mmTeamsService.requestSections(teamId);

		const section = team.orderedSections[0]!;
		await this.mmTeamSectionsService.batchAddItems(
			section,
			[{ objectType: TeamSectionItemType.DATABOARD, objectId: Number(boardId) }],
			{ isInfolayerShown: false, isTrackingDisabled: true },
		);

		return teamId;
	}

	async enableOkrsAddon() {
		await this.billingService.addAddon(BillingAddonType.OKR);
	}
}

declare module '@ember/service' {
	interface Registry {
		'e2e-seed': E2ESeedService;
	}
}
